import type { GameEvent, HeatStressState, Sanctuary, World } from '../types';
import { epochDayOf, dayOf366 } from '../calendar';
import { held } from '../held';
import { fromCenti, recordedC, type SstRecord } from '../sstRecord';
import { ALERT_MEANINGS, NO_HEAT_STRESS, alertLevel, degreeHeatingWeeks, nextHeatStress, type AlertLevel } from '../heatStress';
import { sanctuaryRecord } from './climate';

/**
 * Each sanctuary's reef heat stress, day by day (M46, `heatStress.ts`): after
 * the environment, today's HotSpot is the day's departure less the Maximum
 * Monthly Mean's height above the normal, and the Degree Heating Weeks follow
 * it. Only where the record is daily, as the program's own is: a month's mean
 * cannot show a HotSpot's days (a sanctuary reading its region's record has
 * none, as it has no heatwaves). Nothing here draws.
 */

/** The Maximum Monthly Mean's height above the normal on a day, °C, where the record is daily. */
function mmmAboveNormalC(record: SstRecord, epochDay: number): number | null {
  return record.kind === 'daily' ? fromCenti(record.mmmAboveNormalCentiC[dayOf366(epochDay) - 1] as number) : null;
}

/** The HotSpot, °C, for a departure from the normal on a day, or null where the record is not daily. */
export function hotSpotC(record: SstRecord, departureC: number, epochDay: number): number | null {
  const mmm = mmmAboveNormalC(record, epochDay);
  return mmm === null ? null : held(departureC - mmm);
}

/** The Degree Heating Weeks the log names a reef's crossing of (`ALERT_LEVELS` from level 1: definitions, `heatStress.ts`). */
const LOGGED_DHW = [4, 8, 12, 16, 20];

/**
 * After the environment: each sanctuary with a daily record follows its heat
 * stress, and the log says when it crosses an alert level's Degree Heating
 * Weeks, and when an episode that reached the first is over.
 */
export function stepHeatStress(world: World, events: GameEvent[]): void {
  const today = epochDayOf(world);
  for (const s of world.sanctuaries) {
    const hs = hotSpotC(sanctuaryRecord(s), s.env.seaSurfaceAnomalyC, today);
    if (hs === null) continue;
    const before = s.heatStress;
    const was = degreeHeatingWeeks(before);
    s.heatStress = nextHeatStress(before, hs);
    const now = degreeHeatingWeeks(s.heatStress);
    const crossed = LOGGED_DHW.filter((at) => was < at && now >= at);
    const top = crossed[crossed.length - 1];
    if (top !== undefined) {
      const level = alertLevel(hs, now);
      events.push({
        day: world.day,
        severity: top >= (LOGGED_DHW[1] as number) ? 'critical' : 'warning',
        title: `${level} on the reefs around ${s.site.placeName}`,
        detail: `The heat stress has reached ${top} degree heating weeks (NOAA Coral Reef Watch's measure): ${ALERT_MEANINGS[level]}.`,
      });
    }
    if (now === 0 && was > 0 && before.peakDhw >= (LOGGED_DHW[0] as number)) {
      events.push({
        day: world.day,
        severity: 'info',
        title: `The heat stress on the reefs around ${s.site.placeName} is over`,
        detail: `It reached ${before.peakDhw.toFixed(1)} degree heating weeks at its worst.`,
      });
    }
  }
}

/**
 * The days before a world opens that its heat stress is followed over, so an
 * episode under way opens with its window and its peak: longer than the
 * record's longest episode (MEASURED, `tests/heatStress.test.ts`).
 */
const HEAT_LOOKBACK_DAYS = 365;

/**
 * A sanctuary's heat stress on the day a world opens: followed over the year
 * before it in the record, where the record has the year, so a world that
 * opens in an episode opens in it; else none.
 */
export function openingHeatStress(record: SstRecord, startEpochDay: number): HeatStressState {
  let h: HeatStressState = { ...NO_HEAT_STRESS, countedC: [...NO_HEAT_STRESS.countedC] };
  const from = startEpochDay - HEAT_LOOKBACK_DAYS + 1;
  if (record.kind !== 'daily' || recordedC(record, from) === null || recordedC(record, startEpochDay) === null) return h;
  for (let d = from; d <= startEpochDay; d++) h = nextHeatStress(h, hotSpotC(record, recordedC(record, d) as number, d) as number);
  return h;
}

/** The heat stress the readout shows: today's HotSpot, °C, the Degree Heating Weeks and the alert level, or null where none is followed. */
export function heatStressToday(s: Sanctuary, epochDay: number): { hotSpotC: number; dhw: number; level: AlertLevel } | null {
  const hs = hotSpotC(sanctuaryRecord(s), s.env.seaSurfaceAnomalyC, epochDay);
  if (hs === null) return null;
  const dhw = degreeHeatingWeeks(s.heatStress);
  return { hotSpotC: hs, dhw, level: alertLevel(hs, dhw) };
}
